import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useFormContext } from 'react-hook-form'
import { Box, Table, TableBody, TableCell, TableRow } from '@mui/material'
import { CheckFormType } from 'src/components/molecules/CheckBoxListWithFilter'
import MemberAssignedStoreCheck from 'src/components/molecules/MemberAssignedStoreCheck'
import {
  AppTableHead,
  AppTableRow,
  ChildCell,
  InputCell,
  SelectCell,
  TextCell,
} from 'src/components/tables'
import { RoleEnum, Store as EntityStore } from 'src/slices/services/api'
import {
  InvalidInputErrorMessage,
  RegexEmail,
} from 'src/utils/regularExpression'
import { AllAssignedStoresNotAvailableRoles } from '../../constants'
import { getRoleOptions } from '../../function'
import { IndexedMember } from '../../reducer'
import { activeOptions, roleOptionsForAdminStoreManager } from '../../type'
import style from './styles.module.scss'

export interface MemberFormType {
  name: string
  email: string
  role: RoleEnum
  assignedStores: {
    checkListValues: string[]
    allChecked: boolean
  }
  active: string
}

export interface MembersFormType {
  members: MemberFormType[]
}

export type MembersUpdateTableProps = {
  members: IndexedMember[]
  stores: EntityStore[]
  selfRole: RoleEnum
  orgCode: string
} & React.StyledProps

// This must stay in sync with AppTableRow's fixed height.
const ROW_HEIGHT = 60
const OVERSCAN_ROWS = 5

const MembersUpdateTable: React.StyledFC<MembersUpdateTableProps> = memo(
  ({ members, stores, selfRole, orgCode }: MembersUpdateTableProps) => {
    const formMethods = useFormContext<MembersFormType>()
    const { getValues, setValue, watch } = formMethods
    const [roles, setRoles] = useState<Record<number, RoleEnum>>({})
    const [scrollTop, setScrollTop] = useState(0)
    const [visibleRowCount, setVisibleRowCount] = useState(0)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const hasWarnedUnboundedHeight = useRef(false)

    // スクロールコンテナの高さは利用側のレイアウトで決まる(Update 画面では画面の
    // 残り領域)ため、window の resize では捉えられない変化もある。コンテナ自身の
    // サイズを購読する。描画に効くのは行数なので、高さではなく行数を state に持ち、
    // リサイズ中の再レンダーを行の境界を跨いだときだけに抑える。
    useLayoutEffect(() => {
      const scrollContainer = scrollContainerRef.current
      if (!scrollContainer) return undefined

      const updateVisibleRowCount = () => {
        const { clientHeight } = scrollContainer

        // 高さが確定した親でラップされていないと styles の height: 100% が auto へ
        // 解決され、clientHeight が全行分になって仮想化が働かなくなる。エラーにも
        // ならず見た目もほぼ変わらないまま #3562 のフリーズへ戻るため、開発時のみ
        // 検知して知らせる。スクロールコンテナがビューポートより高いことは、
        // 行数によらずレイアウトの誤りである
        if (
          process.env.NODE_ENV !== 'production' &&
          clientHeight > window.innerHeight &&
          !hasWarnedUnboundedHeight.current
        ) {
          hasWarnedUnboundedHeight.current = true
          // eslint-disable-next-line no-console
          console.warn(
            'MembersUpdateTable: スクロールコンテナがビューポートより高いため、仮想化が機能していません。高さが確定した親でラップしてください。'
          )
        }

        setVisibleRowCount(Math.ceil(clientHeight / ROW_HEIGHT))
      }

      updateVisibleRowCount()
      const resizeObserver = new ResizeObserver(updateVisibleRowCount)
      resizeObserver.observe(scrollContainer)

      return () => resizeObserver.disconnect()
    }, [])

    useEffect(() => {
      const nextRoles: Record<number, RoleEnum> = {}
      members.forEach((member) => {
        nextRoles[member.index] =
          getValues(`members.${member.index}.role`) || member.role
      })
      setRoles(nextRoles)
      setScrollTop(0)
      scrollContainerRef.current?.scrollTo({ top: 0 })
    }, [getValues, members])

    const allStoreIds = useCallback(() => {
      return stores.map((store) => store.id)
    }, [stores])

    useEffect(() => {
      const subscription = watch((value, { name }) => {
        if (!name?.endsWith('role') || !value.members) return

        // roleが変更されたときに、フォームのassignedStoresを強制変更する処理
        const splitName = name.split('.')
        const formIndex = Number(splitName[1])
        const formMembers: MemberFormType[] = value.members as MemberFormType[]
        const nextRole = value.members[formIndex]?.role
        const previousAssignedStoreAllchecked =
          formMembers[formIndex].assignedStores?.allChecked
        if (
          previousAssignedStoreAllchecked &&
          !!nextRole &&
          AllAssignedStoresNotAvailableRoles.includes(nextRole)
        ) {
          setValue(`members.${formIndex}.assignedStores`, {
            checkListValues: selfRole === RoleEnum.Admin ? [] : allStoreIds(),
            allChecked: false,
          })
        }
        if (nextRole === RoleEnum.Admin) {
          setValue(`members.${formIndex}.assignedStores`, {
            checkListValues: [],
            allChecked: true,
          })
        }
        if (nextRole) {
          setRoles((previousRoles) => ({
            ...previousRoles,
            [formIndex]: nextRole,
          }))
        }
      })
      return () => subscription.unsubscribe()
    }, [watch, setValue, selfRole, allStoreIds])

    const startIndex = Math.max(
      0,
      Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS
    )
    const endIndex = Math.min(
      members.length,
      startIndex + visibleRowCount + OVERSCAN_ROWS * 2
    )
    const visibleMembers = members.slice(startIndex, endIndex)
    const topSpacerHeight = startIndex * ROW_HEIGHT
    const bottomSpacerHeight = (members.length - endIndex) * ROW_HEIGHT

    return (
      <Box
        ref={scrollContainerRef}
        className={style.tableContainer}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      >
        <Table>
          <AppTableHead
            columns={[
              { title: '利用ステータス' },
              { title: '権限' },
              { title: '氏名' },
              { title: 'メールアドレス' },
              { title: '担当店舗' },
            ]}
          />
          <TableBody>
            {topSpacerHeight > 0 && (
              <TableRow aria-hidden="true">
                <TableCell
                  className={style.spacerCell}
                  colSpan={5}
                  style={{ height: topSpacerHeight }}
                />
              </TableRow>
            )}
            {visibleMembers.map((member) => {
              const formMember = getValues(`members.${member.index}`)
              const { index } = member
              let assignedStoreIds = []
              let allChecked = false
              const role = roles[index] || formMember?.role || member.role
              if (formMember) {
                assignedStoreIds =
                  formMember.assignedStores?.checkListValues || []
                allChecked = formMember.assignedStores?.allChecked || false
              } else {
                // formがまだ無い時に、ネットワークから取得したデータの設定
                assignedStoreIds =
                  member.assignedStores?.map(
                    (assignedStore) => assignedStore.id
                  ) || []
                allChecked = member.hasAllStoresPermission
              }

              // NOTE: defaultCheckListは、初期値に加え、MemberAssignedStoreCheckはTextCellから置き換わるときに適用される。
              const defaultCheckList: CheckFormType = {
                checkListValues: assignedStoreIds,
                allChecked,
              }
              const disabledAllCheck =
                AllAssignedStoresNotAvailableRoles.includes(role)
              const disableAssignedStore =
                role === RoleEnum.Admin && allChecked === true

              return (
                <AppTableRow key={member.index}>
                  <SelectCell
                    name={`members.${member.index}.active`}
                    options={activeOptions}
                    defaultValue={`${member.active ?? true}`}
                    width={110}
                  />
                  <SelectCell
                    name={`members.${member.index}.role`}
                    width={170}
                    options={
                      selfRole === RoleEnum.AdminStoreManager
                        ? roleOptionsForAdminStoreManager
                        : getRoleOptions(orgCode)
                    }
                    defaultValue={member.role}
                  />
                  <InputCell
                    name={`members.${member.index}.name`}
                    defaultValue={member.name}
                    width={270}
                    required
                    preserveValueOnRemount
                    validationTrigger="onBlur"
                  />
                  {/* 本テーブルは仮想化されており、アンマウント中の行は react-hook-form の
                      submit 検証対象外になる。検証ルールを追加する場合は Update テンプレート
                      handleSubmit の保存時全件検証にも同じルールを追加すること */}
                  <InputCell
                    name={`members.${member.index}.email`}
                    defaultValue={member.email}
                    validations={[
                      {
                        regex: RegexEmail,
                        message: InvalidInputErrorMessage,
                      },
                    ]}
                    width={290}
                    required
                    preserveValueOnRemount
                    validationTrigger="onBlur"
                  />
                  {disableAssignedStore ? (
                    <TextCell width={300} text="全ての店舗" />
                  ) : (
                    <ChildCell>
                      <MemberAssignedStoreCheck
                        width={300}
                        stores={stores}
                        defaultCheckList={defaultCheckList}
                        formName={`members.${member.index}.assignedStores`}
                        defaultTitle="選択してください"
                        becomeErrorIfEmpty
                        disabledAllCheck={disabledAllCheck}
                      />
                    </ChildCell>
                  )}
                </AppTableRow>
              )
            })}
            {bottomSpacerHeight > 0 && (
              <TableRow aria-hidden="true">
                <TableCell
                  className={style.spacerCell}
                  colSpan={5}
                  style={{ height: bottomSpacerHeight }}
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    )
  }
)

MembersUpdateTable.displayName = 'MembersUpdateTable'
export default MembersUpdateTable
